/**
 * useTelemetry — returns the telemetry track function from context.
 *
 * Design
 * ------
 * - When no TelemetryProvider is mounted in the tree the hook returns a
 *   no-op track function.  Primitives are therefore safe to wire
 *   unconditionally; events fire only when a real provider is present.
 * - NEVER throws — defensive by design; telemetry must not break the
 *   product path under any circumstance.
 * - exactOptionalPropertyTypes compliant.
 */

import { useContext } from 'react';
import { TelemetryContext } from './TelemetryProvider';

export type TrackFn = (eventName: string, payload?: Record<string, unknown>) => void;

export type TelemetryHook = {
    track: TrackFn;
};

/** Safe no-op used when no provider is mounted. */
const NOOP_TRACK: TrackFn = () => undefined;

const NOOP_HOOK: TelemetryHook = { track: NOOP_TRACK };

/**
 * Returns `{ track }` from the nearest TelemetryProvider.
 * Falls back to a no-op when called outside a provider — never throws.
 */
export function useTelemetry(): TelemetryHook {
    const ctx = useContext(TelemetryContext);
    if (ctx === null) return NOOP_HOOK;
    return ctx;
}
